import { CANVAS_SIZE } from '@/constant';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, IconButtonProps } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useResizeObserverRef } from 'rooks';

export const MIN_SCALE = 0.075;
export const MAX_WIDTH = CANVAS_SIZE.width * 2;
export const MAX_HEIGHT = CANVAS_SIZE.height * 2;

type Props = {
    minScale?: number;
    padding?: number;
    onNextSlide?: () => void;
    onPreviousSlide?: () => void;
    navigation?: boolean;
};

const SideIconButton: React.FC<{ direction: 'left' | 'right' } & IconButtonProps> = (props) => {
    const width = 48;

    return props.onClick ? (
        <IconButton {...props} sx={{ height: '100%', width: width, borderRadius: 0.5 }}>
            {props.direction === 'right' ? <ChevronRightIcon sx={{ height: 48, width: width }} /> : <ChevronLeftIcon sx={{ height: 48, width: width }} />}
        </IconButton>
    ) : (
        <div style={{ height: '100%', minWidth: width }} />
    );
};

export const SlideContainer: React.FC<PropsWithChildren<Props>> = ({ children, minScale = MIN_SCALE, padding = 72, onNextSlide, onPreviousSlide, navigation = true }) => {
    const offset = padding * 2 + (navigation ? 96 : 0);
    const minWidth = MAX_WIDTH * minScale;
    const minHeight = MAX_HEIGHT * minScale;

    const [height, setHeight] = React.useState('100%');
    const [width, setWidth] = React.useState('auto');

    const [ref] = useResizeObserverRef((entry) => {
        if (!entry || entry.length === 0) {
            return;
        }

        const ewidth = entry[0]?.contentRect.width;
        const eheight = entry[0]?.contentRect.height;

        if (!ewidth || !eheight) {
            return;
        }

        if ((ewidth - offset) / eheight >= 4 / 3) {
            setWidth('auto');
            setHeight('100%');
        } else {
            setWidth('100%');
            setHeight('auto');
        }
    });

    return (
        <div
            style={{
                padding: padding,
                flex: 1,
                justifyContent: navigation ? 'space-between' : 'center',
                alignItems: 'center',
                display: 'flex',
                gap: 16,
            }}
            ref={ref}
        >
            {navigation && <SideIconButton direction='left' onClick={onPreviousSlide} />}
            <div
                style={{
                    aspectRatio: 4 / 3,
                    height: height,
                    width: width,
                    maxWidth: MAX_WIDTH,
                    maxHeight: MAX_HEIGHT,
                    minWidth: minWidth,
                    minHeight: minHeight,
                    position: 'relative',
                }}
                aria-label='canvas'
            >
                {children}
            </div>
            {navigation && <SideIconButton direction='right' onClick={onNextSlide} />}
        </div>
    );
};
